import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID, REQUEST_CONTEXT } from '@angular/core';
import { ConsentServiceName, IServerRequestContext, MetaData } from 'libs/shared/src/models';
import { from, map, merge, Observable } from 'rxjs';
import { CS3_COOKIE_BANNER_INCLUDE, SEO_SNIPPETS_MARKER_NAME } from '../../config/seo.constants';
import { BaseScript } from './base.script';
import { ElfsightScript } from './elfsight/elfsight';
import { GoogleMapsScript } from './maps/google-maps';

/** The page snippets the CMS payload carries; the rest of {@link MetaData} is irrelevant here. */
export type PageScriptMetaData = Partial<Pick<MetaData, 'headerJavaScript' | 'bottomBody' | 'bottomJavaScript'>>;

/**
 * Creates the `dataLayer` the action scripts push into, the same way the GTM container snippet does.
 * Prepended in {@link ScriptService.injectResponseScripts}: without a container — tracking disabled
 * or analytics declined — the bare `dataLayer.push(…)` of an action would throw a `ReferenceError`.
 * Pushing into the queue nobody reads is inert, so this stays free of consent implications.
 */
const DATA_LAYER_BOOTSTRAP = 'window.dataLayer = window.dataLayer || [];';

@Injectable({
  providedIn: 'root'
})
export class ScriptService {
  // Services
  private readonly document = inject(DOCUMENT);
  private readonly elfsightScript = inject(ElfsightScript);
  private readonly googleMapsScript = inject(GoogleMapsScript);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  /** Context of the server render, null in the browser */
  private readonly requestContext = inject(REQUEST_CONTEXT, { optional: true }) as IServerRequestContext | null;

  // Properties & Signals
  /**
   * Content signatures (`src` or inline code) of everything already injected. The backend repeats
   * the global tracking setup (GTM container, GA4 gtag.js, consent defaults) verbatim in every page
   * payload, and {@link injectPageScripts} runs on every navigation — the set keeps that setup at
   * exactly one injection. Action scripts ({@link injectResponseScripts}) bypass it.
   */
  private readonly injectedSignatures = new Set<string>();
  /**
   * Whether the snippets of the first page still have to be taken over from the server-rendered
   * HTML. The server render writes them into the served document and marks that with
   * {@link SEO_SNIPPETS_MARKER_NAME}; the first {@link injectPageScripts} call then only records
   * their signatures. Running them again would bootstrap the GTM container a second time, while
   * skipping them silently would leave the signatures unknown and re-inject the global setup on
   * the next navigation.
   */
  private takeOverServerSnippets = this.isBrowser && !!this.document.querySelector(`meta[name="${SEO_SNIPPETS_MARKER_NAME}"]`);
  /** Record of scripts used in the components by their consent service name */
  private readonly componentScripts: Partial<Record<ConsentServiceName, BaseScript>> = {
    elfsight: this.elfsightScript,
    gmaps: this.googleMapsScript
  };

  // Methods
  /**
   * Loads the scripts of the given consent services in parallel.
   * @param services The consent services whose scripts should be loaded.
   * @returns An observable emitting each service name as its script finishes loading,
   * completing once all of them are done and erroring on the first failure.
   */
  public loadComponentScripts(services: ConsentServiceName[]): Observable<ConsentServiceName> {
    const scriptsToLoad = Object.entries(this.componentScripts).filter(
      (entry): entry is [ConsentServiceName, BaseScript] => !!entry[1] && services.includes(entry[0] as ConsentServiceName)
    );
    return merge(
      ...scriptsToLoad.map(([serviceName, script]) => from(script.loadScript()).pipe(map(() => serviceName as ConsentServiceName)))
    );
  }

  /**
   * Injects the tenant-supplied page snippets (`headerJavaScript`, the `bottomBody` HTML and
   * `bottomJavaScript`). Called on every navigation, because a page payload can carry snippets of
   * its own; the global tracking setup it repeats verbatim each time stays at one injection
   * through the content de-duplication. Scripts bound to an action arrive separately through
   * {@link injectResponseScripts}.
   *
   * `headerHtml` is left out: `SeoService.writeHeaderHtml` writes it.
   * @param metaData The page metadata delivered by the CMS endpoint.
   */
  public injectPageScripts(metaData: PageScriptMetaData | undefined): void {
    if (!metaData) return;
    if (!this.isBrowser) {
      this.writeServerSnippets(metaData);
      return;
    }

    // The snippets of the entry page are already in the document, so the first call only takes
    // their signatures over. Every later navigation injects for real.
    const takeOver = this.takeOverServerSnippets;
    this.takeOverServerSnippets = false;

    this.injectScriptsFromString(metaData.headerJavaScript?.replace(CS3_COOKIE_BANNER_INCLUDE, '') ?? '', 'head', takeOver);
    this.injectBottomBody(metaData.bottomBody, takeOver);
    this.injectScriptsFromString(metaData.bottomJavaScript ?? '', 'bottom', takeOver);
  }

  /**
   * Writes the page snippets into the server-rendered document once: the GTM container boots while the
   * HTML parses instead of after the bundle, and the GTM `<noscript>` reaches visitors without JavaScript.
   * Only with the consent bridge inlined — the snippets call `hasCookie` while the head is parsed and would
   * throw without it, so the browser injects them late instead. {@link SEO_SNIPPETS_MARKER_NAME} tells the
   * browser to take them over rather than run them again.
   * @param metaData The page metadata delivered by the CMS endpoint.
   */
  private writeServerSnippets(metaData: PageScriptMetaData): void {
    if (!this.requestContext?.consentBridgeInlined || this.document.querySelector(`meta[name="${SEO_SNIPPETS_MARKER_NAME}"]`)) return;
    const head = metaData.headerJavaScript?.replace(CS3_COOKIE_BANNER_INCLUDE, '') ?? '';
    const bottom = [metaData.bottomBody, metaData.bottomJavaScript].filter((part) => !!part).join('\n');
    if (!head && !bottom) return;

    const marker = this.document.createElement('meta');
    marker.setAttribute('name', SEO_SNIPPETS_MARKER_NAME);
    marker.setAttribute('content', 'true');
    this.document.head.appendChild(marker);
    // Parsed by the server DOM, which never runs a script; the browser runs them while it parses the page.
    // appendChild: the server DOM knows no append().
    const headContainer = this.document.createElement('div');
    headContainer.innerHTML = head;
    for (const node of Array.from(headContainer.childNodes)) this.document.head.appendChild(node);
    const bottomContainer = this.document.createElement('div');
    bottomContainer.innerHTML = bottom;
    for (const node of Array.from(bottomContainer.childNodes)) this.document.body.appendChild(node);
  }

  /**
   * Executes the code an API response carries in its `JavaScript` field.
   *
   * This is the CS3 delivery path for the scripts produced while an action ran: the backend
   * collects them with `PageMetaData.PopJavaScripts()` and attaches them to the response, which is
   * how the tracking events tied to an action (add_to_cart, purchase, login, …) reach the
   * dataLayer. The field holds bare JavaScript without `<script>` tags, so the code goes into an
   * element built here instead of through {@link injectScriptsFromString}.
   *
   * Never de-duplicated — each response carries only its own action, and adding the same article to
   * the cart twice has to push two events. The element is removed right after it ran: appending
   * executes it synchronously, so keeping it would only grow the DOM for the rest of the session.
   * @param code The `JavaScript` field of the response.
   */
  public injectResponseScripts(code: string): void {
    if (!code || !this.isBrowser) return;

    const script = this.document.createElement('script');
    script.type = 'text/javascript';
    // The action code pushes into `dataLayer` without creating it — that is the container's job, and
    // the container is absent whenever tracking is off or the visitor declined analytics.
    script.textContent = `${DATA_LAYER_BOOTSTRAP}\n${code}`;

    this.document.body.appendChild(script);
    script.remove();
  }

  /**
   * Injects the script tags contained in an HTML string into the document.
   *
   * The string is parsed into an inert document first, so nothing executes before the
   * de-duplication ran and the tag was rebuilt — a parsed `<script>` never runs on its own.
   * @param html The HTML string containing the script tags.
   * @param position Where to append the scripts. Defaults to the head.
   * @param registerOnly Only record the signatures, because the tags are already in the document.
   */
  private injectScriptsFromString(html: string, position: 'head' | 'bottom' = 'head', registerOnly = false): void {
    if (!html) return;

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const target = position === 'head' ? this.document.head : this.document.body;

    parsed.querySelectorAll('script').forEach((originalScript) => {
      const src = originalScript.getAttribute('src');
      if (!this.remember(src ? `src:${src}` : `inline:${originalScript.textContent ?? ''}`)) return;
      if (registerOnly) return;

      // Rebuilt rather than adopted, because a script node moved out of a parsed document stays
      // inert. Every attribute is carried over: the code executes either way, so filtering them
      // would only drop behaviour (nonce, class, id, data-*) without adding safety.
      const script = this.document.createElement('script');
      for (const { name, value } of Array.from(originalScript.attributes)) {
        script.setAttribute(name, value);
      }
      script.textContent = originalScript.textContent;

      target.appendChild(script);
    });
  }

  /**
   * Appends the `bottomBody` HTML block (e.g. the GTM `<noscript>` fallback) to the body.
   * @param html The HTML block delivered by the CMS endpoint.
   * @param registerOnly Only record the signature, because the block is already in the document.
   */
  private injectBottomBody(html: string | undefined, registerOnly = false): void {
    if (!html || !this.remember(`body:${html}`) || registerOnly) return;

    // A `<template>` rather than `DOMParser`: the block usually starts with the GTM `<noscript>`,
    // and a full-document parse is still in head context there — the fallback would land in
    // `parsed.head` and be dropped. Template content is parsed in body context and stays inert.
    const template = this.document.createElement('template');
    template.innerHTML = html;
    this.document.body.append(template.content);
  }

  /**
   * Records a content signature.
   * @param signature The signature of the content about to be injected.
   * @returns `true` when the content is new and should be injected, `false` when it already is.
   */
  private remember(signature: string): boolean {
    if (this.injectedSignatures.has(signature)) return false;
    this.injectedSignatures.add(signature);
    return true;
  }
}
